/** @jsxImportSource @emotion/react */
import React, { MouseEventHandler, useCallback, useContext } from "react";

import { isMobile } from "react-device-detect";

import { useDrag } from "react-dnd";
import { Handle, NodeProps, Position } from "reactflow";

import EmailPNG from "../../assets/images/campaign/email.png";
import SMSPNG from "../../assets/images/campaign/sms.png";
import LetterPNG from "../../assets/images/campaign/envelope.png";

import ModalContext from "./ModalContext";

import { NodeType } from "./SiCaTypes";
import {
  FalseHandlerStyle,
  TrueHandlerStyle,
  CustomNodeStyle,
  StartEndNodeStyle,
  ConnectionNodeStyles,
  deactivatedNodeClass,
  sizeOfHandles,
  hasBorder,
} from "./SiCaStyles";
import { AllNodeTypes } from "./SiCaData";
import { useDoubleTap } from "use-double-tap";

// Node Components
type CustomNodeType = NodeProps<NodeType>;

const CustomNode: React.FC<CustomNodeType> = ({ data, selected }) => {
  const { openModal } = useContext(ModalContext);

  const openModalHandler = useCallback<MouseEventHandler>(
    () => openModal(data),
    [data, openModal]
  );

  const bind = useDoubleTap(isMobile ? openModalHandler : null);

  return (
    <div
      css={!data.isActive ? deactivatedNodeClass : undefined}
      style={{
        ...CustomNodeStyle,
        borderWidth: hasBorder(selected),
      }}
      onDoubleClick={openModalHandler}
      {...bind}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={sizeOfHandles(isMobile, "target")}
      />
      <p
        style={{
          direction: "rtl",
          fontSize: "0.85rem",
          textAlign: "center",
          padding: "3px 5px",
          // limit number of lines
          maxHeight: "3.3em",
          lineHeight: "1.4em",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {data.title}
      </p>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ ...TrueHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ ...FalseHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
    </div>
  );
};

const StartNode: React.FC<NodeProps> = ({ selected }) => {
  return (
    <div
      style={{
        ...StartEndNodeStyle,
        borderWidth: hasBorder(selected),
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={sizeOfHandles(isMobile)}
      />
      <p>شروع</p>
    </div>
  );
};

const EndNode: React.FC<NodeProps> = ({ selected }) => {
  return (
    <div
      style={{
        ...StartEndNodeStyle,
        borderWidth: hasBorder(selected),
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={sizeOfHandles(isMobile, "target")}
      />
      <p>پایان</p>
    </div>
  );
};

const EmailNode: React.FC<CustomNodeType> = ({ selected, data }) => {
  const { openModal } = useContext(ModalContext);

  const openModalHandler = useCallback<MouseEventHandler>(
    () => openModal(data),
    [data, openModal]
  );

  const bind = useDoubleTap(isMobile ? openModalHandler : null);

  return (
    <div
      css={!data.isActive ? deactivatedNodeClass : undefined}
      style={{
        ...ConnectionNodeStyles("#F48FB1", "#D81B60", "#880E4F"),
        borderWidth: hasBorder(selected),
      }}
      onDoubleClick={openModalHandler}
      {...bind}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={sizeOfHandles(isMobile, "target")}
      />
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={EmailPNG}
          alt={"ایمیل"}
        />
        <p style={{ fontSize: "0.85rem" }}>ایمیل</p>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ ...TrueHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ ...FalseHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
    </div>
  );
};

const SMSNode: React.FC<CustomNodeType> = ({ selected, data }) => {
  const { openModal } = useContext(ModalContext);

  const openModalHandler = useCallback<MouseEventHandler>(
    () => openModal(data),
    [data, openModal]
  );

  const bind = useDoubleTap(isMobile ? openModalHandler : null);

  return (
    <div
      css={!data.isActive ? deactivatedNodeClass : undefined}
      style={{
        ...ConnectionNodeStyles("#80CBC4", "#00897B", "#004D40"),
        borderWidth: hasBorder(selected),
      }}
      onDoubleClick={openModalHandler}
      {...bind}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={sizeOfHandles(isMobile, "target")}
      />
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={SMSPNG}
          alt={"پیامک"}
        />
        <p style={{ fontSize: "0.85rem" }}>پیامک</p>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ ...TrueHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ ...FalseHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
    </div>
  );
};

const LetterNode: React.FC<CustomNodeType> = ({ selected, data }) => {
  const { openModal } = useContext(ModalContext);

  const openModalHandler = useCallback<MouseEventHandler>(
    () => openModal(data),
    [data, openModal]
  );

  const bind = useDoubleTap(isMobile ? openModalHandler : null);

  return (
    <div
      css={!data.isActive ? deactivatedNodeClass : undefined}
      style={{
        ...ConnectionNodeStyles("#9FA8DA", "#3949AB", "#1A237E"),
        borderWidth: hasBorder(selected),
      }}
      onDoubleClick={openModalHandler}
      {...bind}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={sizeOfHandles(isMobile, "target")}
      />
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={LetterPNG}
          alt={"نامه"}
        />
        <p style={{ fontSize: "0.85rem" }}>نامه</p>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ ...TrueHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ ...FalseHandlerStyle, ...sizeOfHandles(isMobile) }}
      />
    </div>
  );
};

// UI Components
const CustomNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.Custom,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        minWidth: 100,
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...CustomNodeStyle,
        ...style,
      }}
      {...props}
    >
      <p style={{ fontSize: "0.85rem" }}>عملیات فروش</p>
    </div>
  );
};

const StartNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.Start,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...StartEndNodeStyle,
        ...style,
      }}
      {...props}
    >
      <p>شروع</p>
    </div>
  );
};

const EndNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.End,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...StartEndNodeStyle,
        ...style,
      }}
      {...props}
    >
      <p>پایان</p>
    </div>
  );
};

const EmailNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.Email,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...ConnectionNodeStyles("#F48FB1", "#D81B60", "#880E4F"),
        ...style,
      }}
      {...props}
    >
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={EmailPNG}
          alt={"ایمیل"}
        />
        <p style={{ fontSize: "0.85rem" }}>ایمیل</p>
      </div>
    </div>
  );
};
const SMSNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.SMS,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...ConnectionNodeStyles("#80CBC4", "#00897B", "#004D40"),
        ...style,
      }}
      {...props}
    >
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={SMSPNG}
          alt={"پیامک"}
        />
        <p style={{ fontSize: "0.85rem" }}>پیامک</p>
      </div>
    </div>
  );
};
const LetterNodeUI: React.FC<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
> = ({ style, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Box",
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    item: {
      name: AllNodeTypes.Letter,
    },
  }));
  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        ...ConnectionNodeStyles("#9FA8DA", "#3949AB", "#1A237E"),
        ...style,
      }}
      {...props}
    >
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            zIndex: 0,
            marginBottom: "0.3rem",
          }}
          src={LetterPNG}
          alt={"نامه"}
        />
        <p style={{ fontSize: "0.85rem" }}>نامه</p>
      </div>
    </div>
  );
};

export {
  // Node Components
  CustomNode,
  StartNode,
  EndNode,
  EmailNode,
  SMSNode,
  LetterNode,
  // UI Components
  CustomNodeUI,
  StartNodeUI,
  EndNodeUI,
  EmailNodeUI,
  SMSNodeUI,
  LetterNodeUI,
};
